import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  updatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  const loadUserProfile = useCallback(async () => {
    if (!user) return;

    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      // If settings don't exist, add them with defaults
      if (!data.settings) {
        const updatedData = {
          ...data,
          settings: {
            available_playbooks: 5
          }
        };
        await setDoc(docRef, updatedData);
        setUserProfile(updatedData);
      } else {
        setUserProfile(data);
      }
    } else {
      // Create profile if it doesn't exist
      const profileData = {
        email: user.email,
        displayName: user.displayName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        settings: {
          available_playbooks: 5
        }
      };
      await setDoc(docRef, profileData);
      setUserProfile(profileData);
    }
  }, [user]);

  async function signup(email, password, displayName) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update profile with display name
    await updateProfile(userCredential.user, { displayName });
    
    // Create user profile document in Firestore
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email,
      displayName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      settings: {
        available_playbooks: 5
      }
    });

    return userCredential;
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function updateUserProfile(data) {
    const updates = {};
    const authUpdates = {};

    if (data.displayName) {
      authUpdates.displayName = data.displayName;
      updates.displayName = data.displayName;
    }

    if (data.email && data.email !== user.email) {
      await updateEmail(user, data.email);
      updates.email = data.email;
    }

    if (data.password) {
      await updatePassword(user, data.password);
    }

    if (Object.keys(authUpdates).length > 0) {
      await updateProfile(user, authUpdates);
    }

    if (Object.keys(updates).length > 0) {
      updates.updatedAt = new Date().toISOString();
      await setDoc(doc(db, 'users', user.uid), updates, { merge: true });
      await loadUserProfile(); // Reload the profile
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadUserProfile();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [loadUserProfile]);

  const value = {
    user,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 